import { useEffect, useMemo, useState } from 'react';
import { shouldRenderBannerPrefetched } from '@atlassian/browser-storage-controls';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useConsentBannerUserPreferencesResource,
	type JiraPrefetchedConsentPreferenceData,
} from '@atlassian/jira-router-resources-cookie-consent/src/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { BANNER_NAME } from '../constants.tsx';
import {
	isInIframe,
	useIsInIframe,
	useCookieConsentBannerSettings,
} from '../services/preferences/index.tsx';

export const shouldRenderBanner = (data: JiraPrefetchedConsentPreferenceData | null) => {
	try {
		/**
		 * If we are using the cookie data to verify if we show the banner, and we don't use ATLASSIAN_SHOW_BANNER_COOKIE_KEY
		 * we end up in a situation where the banner is shown,
		 * then the client renders ConsentBanner from '@atlassian/browser-storage-controls'
		 * which then checks that cookie and if it's false
		 * and then we dismiss the banner anyway
		 *
		 * This is because the cookie consent logic does not set the 'fallback' cookie when we prefetch the data
		 * Because of this, shouldRenderBannerPrefetched will always end up returning true if we prefetch
		 *
		 * If we look at the getShowBanner code platform/packages/policy-platform/browser-storage-controls/src/ui/consent-banner/use-banner-visibility/index.ts
		 * We can see that it will use this show banner cookie to determine if the banner should be shown
		 * as long as we aren't in localConsentMode (which we aren't since we do not pass localConsentMode=true to ConsentBanner
		 * in jira/src/packages/platform/ui/banners/cookie-consent/src/ui/banner/CookieConsentBanner.tsx)
		 *
		 * So we can use the ATLASSIAN_SHOW_BANNER_COOKIE_KEY to determine if the banner should be shown instead if it is set
		 */
		if (data?.shouldShowBanner !== undefined) {
			return data.shouldShowBanner;
		}
		if (fg('fedramp-jira-banner-fix')) {
			return data === null || shouldRenderBannerPrefetched(data);
		}
		return data && shouldRenderBannerPrefetched(data);
	} catch (e) {
		return true;
	}
};

export const useBannerCookieConsent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isDismissed, height, hasInit }, { onShow, init }] = useCookieConsentBannerSettings();

	const { data, loading, error } = useConsentBannerUserPreferencesResource();

	const isIframe: boolean = fg('jfp-magma-fix-cookie-banner-hydration')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsInIframe()
		: isInIframe();

	// This is the initial state of the banner esp in ssr land
	const [initialShouldShow] = useState(() => {
		if (loading) {
			return false;
		}
		if (error) {
			return true;
		}
		return Boolean(shouldRenderBanner(data) && !isIframe);
	});

	const shouldShow = useMemo(
		() => (hasInit ? !isDismissed : initialShouldShow),
		[hasInit, initialShouldShow, isDismissed],
	);

	useEffect(() => {
		if (hasInit || loading) {
			return;
		}
		// Now that we're in the client render, let's re-verify if we should show the banner
		// Eg to verify if we are in an iframe
		const shouldRender = Boolean(error || (shouldRenderBanner(data) && !isIframe));
		init(!shouldRender);

		// We should verify how often we end up having a mismatch between the initial render (probably ssr) and init render (client)
		if (!fg('killswitch-analytics-ssr-cookie-consent-banner')) {
			if (initialShouldShow !== shouldRender) {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'cookieConsentBanner showBannerMismatch',
					{
						isInIframe: isIframe,
						initialShouldShow,
						initShouldShow: shouldRender,
					},
				);
			}
		}
	}, [
		createAnalyticsEvent,
		data,
		error,
		hasInit,
		init,
		initialShouldShow,
		isIframe,
		loading,
		onShow,
	]);

	return useMemo(
		() => ({
			isEnabled: shouldShow,
			name: BANNER_NAME,
			height,
		}),
		[shouldShow, height],
	);
};
